import React from 'react';
import { Modal, Progress, List, Typography, Button, Space } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface ProgressItem {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress?: number;
}

interface ProgressModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  overallProgress: number;
  items: ProgressItem[];
  isCompleted: boolean;
  isError: boolean;
  successMessage?: string;
  errorMessage?: string;
}

const ProgressModal: React.FC<ProgressModalProps> = ({
  visible,
  onClose,
  title,
  overallProgress,
  items,
  isCompleted,
  isError,
  successMessage,
  errorMessage,
}) => {
  const getStatusIcon = (status: ProgressItem['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'processing':
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
      case 'error':
        return <CheckCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#d9d9d9' }} />;
    }
  };

  const getStatusText = (status: ProgressItem['status']) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing...';
      case 'error':
        return 'Error';
      default:
        return 'Pending';
    }
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          {isCompleted && (
            <Button type="primary" onClick={onClose}>
              Close
            </Button>
          )}
          {!isCompleted && !isError && (
            <Button onClick={onClose} icon={<CloseOutlined />}>
              Cancel
            </Button>
          )}
        </Space>
      }
      width={600}
      closable={isCompleted || isError}
      maskClosable={isCompleted || isError}
    >
      <div style={{ marginBottom: 24 }}>
        <Text strong>Overall Progress</Text>
        <Progress 
          percent={overallProgress} 
          status={isError ? 'exception' : isCompleted ? 'success' : 'active'}
          strokeColor={isCompleted ? '#52c41a' : '#1890ff'}
        />
      </div>

      {isCompleted && successMessage && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          backgroundColor: '#f6ffed', 
          border: '1px solid #b7eb8f', 
          borderRadius: 6 
        }}>
          <Text style={{ color: '#52c41a' }}>{successMessage}</Text>
        </div>
      )}

      {isError && errorMessage && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          backgroundColor: '#fff2f0', 
          border: '1px solid #ffccc7', 
          borderRadius: 6 
        }}>
          <Text style={{ color: '#ff4d4f' }}>{errorMessage}</Text>
        </div>
      )}

      <div>
        <Text strong>File Processing Details</Text>
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={getStatusIcon(item.status)}
                title={item.name}
                description={
                  <Space>
                    <Text type="secondary">{getStatusText(item.status)}</Text>
                    {item.progress !== undefined && item.status === 'processing' && (
                      <Text type="secondary">({item.progress}%)</Text>
                    )}
                  </Space>
                }
              />
              {item.status === 'processing' && item.progress !== undefined && (
                <Progress 
                  percent={item.progress} 
                  size="small" 
                  style={{ width: 100 }}
                  showInfo={false}
                />
              )}
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default ProgressModal;
